import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { validateNin } from '@application/validators';
import { ButtonArrange, CellulaFormSubmitData, Flex, Form, InputRadio, InputText, useFormContext } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Title } from '@component/title.component';
import { TopBar } from '@component/top_bar.component';
import { RepresentablePatientsPicker } from '@feature/representation/representable-patients-picker.component';
import { type RepresentablePatient, useRespondentRepresentation } from '@feature/representation/use-respondent-representation';
import { useResolve, useResolveController, withNewContainer } from '@visiba/instantiation';
import { useEffect, useRef } from 'react';
import { RedRobinScreen, ViewRedRobinAnalyticActions, ViewRedRobinController } from '../view_red_robin.controller';
import { Above110Dialog } from './_above_110_dialog.component';
import { useScreenWhoIsRespondant } from './use-screen-who-is-respondant';

export const ScreenWhoIsRespondant = withNewContainer(function WhoIsRespondant() {
	const screen = useScreenWhoIsRespondant();
	const { continueToRedRobin } = screen;
	const viewController = useResolveController(ViewRedRobinController);
	const analyticService = useResolve(AnalyticService);
	const analytic = useAnalyticContext();
	const viewData = screen.data;
	const allowRepresentative = viewController.flowData.value.value?.allowRepresentative ?? false;
	const {
		currentlyRepresentedPatient,
		representablePatients,
		representablePatientsError,
		isLoadingRepresentablePatients,
		startRepresentation,
	} = useRespondentRepresentation({ resolveCurrentRepresentation: true });
	const hasAutoCreatedSessionRef = useRef(false);

	useEffect(() => {
		if (representablePatientsError) {
			viewController.goToScreen(RedRobinScreen.Error, { replace: true });
		}
	}, [representablePatientsError, viewController]);

	// Resolve verified representation before considering the legacy respondent
	// input. The backend obtains the represented patient's details from the
	// authenticated identity, so no manual NIN or date of birth is needed.
	useEffect(() => {
		if (isLoadingRepresentablePatients || representablePatientsError || hasAutoCreatedSessionRef.current) {
			return;
		}

		if (currentlyRepresentedPatient) {
			hasAutoCreatedSessionRef.current = true;
			analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.AutoSetRespondentTypeRepresentative);
			void viewController.createSession({
				AsUnverifiedRepresentativeForNin: null,
				DateOfBirth: null,
				InformationGatheringSubmissionId: null,
			});
			return;
		}

		if (!allowRepresentative) {
			hasAutoCreatedSessionRef.current = true;
			analyticService.trackEvent(AnalyticCategories.RedRobin, ViewRedRobinAnalyticActions.DoesNotAllowRepresentative);
			continueToRedRobin('self', { dateOfBirth: '', nhsNumber: '' });
		}
	}, [
		allowRepresentative,
		isLoadingRepresentablePatients,
		representablePatientsError,
		currentlyRepresentedPatient,
		continueToRedRobin,
		analyticService,
		viewController,
	]);

	if (representablePatientsError) {
		return null;
	}

	function handleSubmit(data: CellulaFormSubmitData) {
		screen.submit({
			respondentType: data.respondentType === 'representative' ? 'representative' : 'me',
			patientNin: data.patientNin ?? null,
			dateOfBirth: data.dateOfBirth ?? null,
		});
	}

	function handleInvalidSubmit() {
		analytic.trackEvent(ViewRedRobinAnalyticActions.FormIsInvalid);
	}

	if (viewData.ninPlaceholder === '') {
		return null;
	}

	// Avoid rendering legacy representation input until identity resolution has
	// completed, and while either automatic path is being started.
	if (isLoadingRepresentablePatients || currentlyRepresentedPatient || !allowRepresentative) {
		return null;
	}

	return (
		<>
			<TopBar.Content />

			<Flex grow={1} gap={tokens.spacing.x2}>
				<Form.Root
					options={{
						defaultValues: {
							respondentType: 'me',
						},
						mode: 'onBlur',
					}}
					onSubmit={handleSubmit}
					onInvalid={handleInvalidSubmit}
				>
					<Flex grow={1} gap={tokens.spacing.x2}>
						<Title title={withTranslation('View.Booking.ScreenWhoIsRespondant.Title')} titleSize='small' />
						<WhoIsRespondantFormInputs
							screen={screen}
							representablePatients={representablePatients}
							onRepresentablePatientSelected={(person) => startRepresentation(person.Id)}
						/>
					</Flex>

					<ButtonArrange>
						<Form.SubmitButton onClick={() => analytic.trackEvent('Clicked next button')}>
							{withTranslation('PW.Common.Next')}
						</Form.SubmitButton>
					</ButtonArrange>
				</Form.Root>
			</Flex>
		</>
	);
});

interface WhoIsRespondantFormInputsProps {
	screen: ReturnType<typeof useScreenWhoIsRespondant>;
	representablePatients: RepresentablePatient[];
	onRepresentablePatientSelected: (person: RepresentablePatient) => void;
}

interface InputPresentation {
	label: string;
	description?: string;
	placeholder?: string;
}

interface WhoIsRespondantPresentation {
	patientNin: InputPresentation;
	dateOfBirth: InputPresentation;
}

function getWhoIsRespondantPresentation(
	viewData: ReturnType<typeof useScreenWhoIsRespondant>['data'],
	isNhsAppPilotEnabled: boolean,
): WhoIsRespondantPresentation {
	if (isNhsAppPilotEnabled) {
		return {
			patientNin: {
				label: withTranslation('View.RedRobin.ScreenWhoIsRespondant.NhsApp.Input.Label.NhsNumber'),
			},
			dateOfBirth: {
				label: withTranslation('View.RedRobin.ScreenWhoIsRespondant.NhsApp.Input.Label.DateOfBirth'),
				description: withTranslation('View.RedRobin.ScreenWhoIsRespondant.NhsApp.Input.Description.DateOfBirthFormat'),
			},
		};
	}

	const patientNin = viewData.useNhsNumber
		? {
				label: withTranslation('Form.Input.Label.NhsNumber'),
				description: withTranslation('View.RedRobin.ScreenWhoIsRespondant.RepresentativeNhsNumber.Description'),
				placeholder: viewData.ninPlaceholder,
			}
		: {
				label: withTranslation('Form.Input.Label.Nin'),
				description: withTranslation('View.RedRobin.ScreenWhoIsRespondant.RepresentativeNin.Description'),
				placeholder: viewData.ninPlaceholder,
			};

	return {
		patientNin,
		dateOfBirth: {
			label: withTranslation('Form.Input.Label.DateOfBirth'),
			placeholder: viewData.dateOfBirthPlaceholder,
		},
	};
}

function WhoIsRespondantFormInputs({ screen, representablePatients, onRepresentablePatientSelected }: WhoIsRespondantFormInputsProps) {
	const viewController = useResolveController(ViewRedRobinController);
	const analytic = useAnalyticContext();
	const isNhsAppPilotEnabled = useFeatureFlag('PatientWeb.NHSAppPilot');
	const formContext = useFormContext();
	const respondentType = formContext?.control.watch('respondentType');

	const viewData = screen.data;
	const flowData = viewController.getFlowData();
	const hasRepresentablePatients = representablePatients.length > 0;
	const presentation = getWhoIsRespondantPresentation(viewData, isNhsAppPilotEnabled);

	function handleRespondentTypeChange() {
		const value = formContext?.control.getValues('respondentType');
		analytic.trackEvent(`Changed "RespondentType" to "${value}"`);
	}

	function handlePatientNinChange() {
		analytic.trackEvent(`Changed "PatientNin"`);
	}

	return (
		<>
			<Flex gap={tokens.spacing.x2}>
				<InputRadio.Root
					label={withTranslation('View.RedRobin.ScreenWhoIsRespondant.Input.Label.WhoIsRespondant')}
					hideLabel={isNhsAppPilotEnabled}
					variant='outlined'
					required
					name='respondentType'
					options={{
						required: I18nAndL10n.translate('View.RedRobin.ScreenWhoIsRespondant.ValidationError.Required.WhoIsRespondant'),
					}}
					onChange={handleRespondentTypeChange}
				>
					<InputRadio.Item label={withTranslation('View.RedRobin.ScreenWhoIsRespondant.Option.Me')} value='me' />
					<InputRadio.Item
						label={withTranslation('View.RedRobin.ScreenWhoIsRespondant.Option.Representative')}
						value='representative'
					/>
				</InputRadio.Root>
				{respondentType === 'representative' ? (
					<>
						{hasRepresentablePatients ? (
							<RepresentablePatientsPicker representablePatients={representablePatients} onSelect={onRepresentablePatientSelected} />
						) : null}
						<InputText
							label={presentation.patientNin.label}
							name='patientNin'
							required
							description={presentation.patientNin.description}
							placeholder={presentation.patientNin.placeholder}
							onBlur={handlePatientNinChange}
							options={{
								required: viewData.useNhsNumber
									? I18nAndL10n.translate('Form.ValidationError.Required.NhsNumber')
									: I18nAndL10n.translate('Form.ValidationError.Required.Nin'),
								validate: {
									nin: (value) => {
										return (
											validateNin(flowData.currentCountry, value) ||
											(viewData.useNhsNumber
												? I18nAndL10n.translate('Form.ValidationError.Format.NhsNumber')
												: I18nAndL10n.translate('Form.ValidationError.Format.Nin'))
										);
									},
									age: (value) => {
										return screen.ninDateIsInThePast(value) || I18nAndL10n.translate('Form.ValidationError.Format.Nin');
									},
								},
							}}
						/>
						{!viewData.countryNinHasAgeSupport ? (
							<InputText
								required
								options={{
									required: withTranslation('Form.ValidationError.Required.DateOfBirth'),
									validate: {
										dateOfBirth: (value) => {
											return screen.validateDateOfBirth(value) || withTranslation('Form.ValidationError.Required.DateOfBirth');
										},
									},
								}}
								description={presentation.dateOfBirth.description}
								placeholder={presentation.dateOfBirth.placeholder}
								name='dateOfBirth'
								label={presentation.dateOfBirth.label}
							/>
						) : null}
					</>
				) : null}
			</Flex>
			<Above110Dialog
				isOpen={screen.showAbove110Dialog}
				onClose={screen.closeAbove110Dialog}
				onContinue={screen.continueWithMessaging}
			/>
		</>
	);
}
